import React from "react";
import { Link, Navigate } from "react-router-dom";

import type { EquityDashboardRow, Portfolio, PortfolioAction, PortfolioRow } from "@features/investments/api";
import { GlossaryTerm } from "@features/investments/glossary/term";
import { InvestmentsWorkspaceShell } from "@features/investments/components/workspace-shell";
import { InvestmentsLoadingScreen, InvestmentsPageState } from "@features/investments/components/page-state";
import { useInvestmentEventsQuery, useInvestmentsAccessQuery, useInvestmentsDashboardQuery, useInvestmentsPortfolioQuery } from "@features/investments/queries";
import { useActivePortfolioSelection } from "@features/investments/state";
import { equityRoute } from "@features/investments/routes";
import {
  formatBasisPointsNullable,
  formatCountShare,
  formatCurrencyNullable,
  formatPercentNullable,
  formatScoreValue,
  pill,
  worstRatePressureFamilies,
} from "@features/investments/screens/workspace-shared";
import "@views/investments/index.scss";

type AugmentedPortfolioRow = PortfolioRow & {
  score: number | null;
  companyName: string;
  screeningDisposition: string | null;
  dataConfidence: string | null;
};

const InvestmentsDashboard = (): JSX.Element => {
  const accessQuery = useInvestmentsAccessQuery();
  const dashboardQuery = useInvestmentsDashboardQuery();
  const isGod = accessQuery.data?.isGod === true;
  const portfolioQuery = useInvestmentsPortfolioQuery(isGod);

  const loading = accessQuery.isLoading || dashboardQuery.isLoading || (isGod && portfolioQuery.isLoading);
  const dashboard = dashboardQuery.data;
  const portfolios = portfolioQuery.data?.portfolios ?? [];
  const [selectedPortfolioID, setSelectedPortfolioID] = useActivePortfolioSelection(portfolios.map((portfolio) => portfolio.id));
  const selectedPortfolio = React.useMemo(
    () => portfolios.find((portfolio) => portfolio.id === selectedPortfolioID) ?? null,
    [portfolios, selectedPortfolioID],
  );
  const eventTickers=selectedPortfolio?.tickers??[];
  const eventsQuery=useInvestmentEventsQuery(eventTickers,isGod&&eventTickers.length>0);
  const ratesSummary = dashboard?.equityRatesSummary ?? null;
  const pressuredFamilies = React.useMemo(() => worstRatePressureFamilies(ratesSummary, 3), [ratesSummary]);

  const equityByTicker = React.useMemo(() => {
    const entries = (dashboard?.equityRows ?? []).map((row) => [row.ticker.toUpperCase(), row] as const);
    return new Map(entries);
  }, [dashboard?.equityRows]);

  const candidateRows = React.useMemo(
    () => augmentPortfolioRows(selectedPortfolio, equityByTicker, ["ACTIVE_RESEARCH", "CANDIDATE"]),
    [equityByTicker, selectedPortfolio],
  );
  const ownedRows = React.useMemo(
    () => augmentPortfolioRows(selectedPortfolio, equityByTicker, ["OWNED"]),
    [equityByTicker, selectedPortfolio],
  );
  const overdueReviews = React.useMemo(
    () => sortQueueRows(candidateRows.concat(ownedRows).filter((row) => isOverdue(row.nextReviewAt))),
    [candidateRows, ownedRows],
  );
  const upcomingReviews = React.useMemo(
    () => sortQueueRows(candidateRows.concat(ownedRows).filter((row) => isUpcoming(row.nextReviewAt))),
    [candidateRows, ownedRows],
  );
  const nearBuyCandidates = React.useMemo(
    () => sortQueueRows(candidateRows.filter((row) => isNearBuyRange(row.currentPrice, row.targetBuyPrice))),
    [candidateRows],
  );
  const ownedNeedsAttention = React.useMemo(
    () => sortQueueRows(ownedRows.filter((row) => row.recommendedAction !== "HOLD" || row.warningCount > 0 || row.engineAction === "AVOID")),
    [ownedRows],
  );

  if (loading) {
    return <InvestmentsLoadingScreen message="Loading operator command..." />;
  }

  if (accessQuery.error) {
    return <InvestmentsPageState message={`Failed to resolve investments access: ${accessQuery.error.message}`} />;
  }

  if (dashboardQuery.error) {
    return <InvestmentsPageState message={`Failed to load command workspace: ${dashboardQuery.error.message}`} />;
  }

  if (!isGod) {
    return <Navigate to="/investments/coverage" replace />;
  }

  return (
    <InvestmentsWorkspaceShell
      title="Command"
      description="Operator Mode keeps reviews, candidate timing, owned-position drift, and macro pressure on one page."
      generatedAt={dashboard?.generatedAt}
    >
      <section className="investments-card workflow-card workflow-card-header">
        <div className="workflow-header-copy">
          <div className="section-eyebrow">Operator Mode</div>
          <h2>Run one active portfolio as the current decision surface</h2>
          <p>Use the selected portfolio to drive review cadence, near-buy monitoring, and owned-position attention queues.</p>
        </div>
        <div className="workflow-header-controls">
          <label className="workflow-select-field" htmlFor="active-portfolio-select">
            <span>Portfolio</span>
            <select
              id="active-portfolio-select"
              className="workflow-header-select"
              value={selectedPortfolioID ?? ""}
              onChange={(event) => setSelectedPortfolioID(event.target.value ? Number(event.target.value) : null)}
            >
              {portfolios.length === 0 && <option value="">No saved portfolios</option>}
              {portfolios.map((portfolio) => (
                <option key={portfolio.id} value={portfolio.id}>
                  {portfolio.name}
                </option>
              ))}
            </select>
          </label>
          <Link className="workflow-header-controls-button" to="/investments/portfolio">
            Open Portfolio
          </Link>
        </div>
      </section>

      {selectedPortfolio&&<section className="workflow-section"><div className="workflow-section-header"><div className="workflow-section-copy workflow-section-copy-plain"><h2>Upcoming catalysts</h2><p>Cached FMP earnings, dividend, and split events for the selected portfolio. Estimates are external expectations, not house forecasts.</p></div>{eventsQuery.data&&<div className="workflow-section-meta">Through {eventsQuery.data.endDate}</div>}</div><section className="investments-card workflow-card catalyst-list">{eventsQuery.isLoading?<div className="workflow-empty">Loading cached catalysts…</div>:eventsQuery.error?<div className="workflow-empty">Catalyst data is unavailable until the next scheduled refresh.</div>:eventsQuery.data?.events.length?eventsQuery.data.events.slice(0,12).map(event=><article key={event.id} className="catalyst-row"><time>{event.date}</time><Link to={equityRoute(event.ticker)}>{event.ticker}</Link><strong>{event.type}</strong><span>{event.type==="earnings"&&event.epsEstimated!=null?`External EPS estimate ${event.epsEstimated.toFixed(2)}`:event.type==="dividend"&&event.dividend!=null?`Dividend ${event.dividend.toFixed(2)}`:event.type==="split"&&event.splitNumerator!=null?`${event.splitNumerator}:${event.splitDenominator??1} split`:"Details pending"}</span></article>):<div className="workflow-empty">No upcoming catalysts in the cached window.</div>}</section></section>}

      {ratesSummary && (
        <section className="workflow-section">
          <div className="workflow-section-header">
            <div className="workflow-section-copy workflow-section-copy-plain">
              <h2>Macro Snapshot</h2>
              <p>Keep Treasury hurdles visible while you interpret candidate and owned-position queues.</p>
            </div>
            <div className="workflow-section-meta">
              3M {formatPercentNullable(ratesSummary.threeMonthBill)} | 10Y {formatPercentNullable(ratesSummary.tenYearNote)}
            </div>
          </div>
          <section className="investments-card workflow-card workflow-rates-summary-card">
            <div className="workflow-rates-summary-grid">
              <article className="workflow-rates-summary-metric">
                <div className="workflow-metric-label"><GlossaryTerm label="Beats 3M" variant="metric" /></div>
                <div className="workflow-metric-value">{formatCountShare(ratesSummary.beatingThreeMonthCount, ratesSummary.validCurrentFcfYieldCount)}</div>
              </article>
              <article className="workflow-rates-summary-metric">
                <div className="workflow-metric-label"><GlossaryTerm label="Beats 10Y" variant="metric" /></div>
                <div className="workflow-metric-value">{formatCountShare(ratesSummary.beatingTenYearCount, ratesSummary.validCurrentFcfYieldCount)}</div>
              </article>
              <article className="workflow-rates-summary-metric">
                <div className="workflow-metric-label"><GlossaryTerm label="Median FCF-10Y" variant="metric" /></div>
                <div className="workflow-metric-value">{formatBasisPointsNullable(ratesSummary.medianCurrentSpreadToTenYear)}</div>
              </article>
              <article className="workflow-rates-summary-metric">
                <div className="workflow-metric-label"><GlossaryTerm label="Median Buy Yld-10Y" variant="metric" /></div>
                <div className="workflow-metric-value">{formatBasisPointsNullable(ratesSummary.medianBuyThresholdSpreadToTenYear)}</div>
              </article>
            </div>
            <div className="workflow-rates-pressure-list">
              <div className="workflow-metric-label"><GlossaryTerm label="Most Pressured Families" variant="metric" /></div>
              {pressuredFamilies.length === 0 ? (
                <div className="workflow-empty">No family pressure data yet.</div>
              ) : (
                <div className="workflow-rates-pressure-items">
                  {pressuredFamilies.map((family) => (
                    <span key={family.key} className="workflow-rates-pressure-item">
                      {family.name}: {formatBasisPointsNullable(family.medianCurrentSpreadToTenYear)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </section>
        </section>
      )}

      {portfolioQuery.error && <InvestmentsPageState message={`Failed to load portfolios: ${portfolioQuery.error.message}`} />}

      {!portfolioQuery.error && (
        <>
          <section className="workflow-section">
            <div className="workflow-section-header">
              <div className="workflow-section-copy workflow-section-copy-plain">
                <h2>Review Queue</h2>
                <p>Review dates become a first-class queue instead of buried metadata.</p>
              </div>
              {selectedPortfolio && <div className="workflow-section-meta">{selectedPortfolio.name}</div>}
            </div>
            {selectedPortfolio == null ? (
              <div className="workflow-empty">Create a portfolio in Portfolio to start the operator queue.</div>
            ) : (
              <div className="workflow-grid workflow-grid-command">
                <WorkflowQueueColumn
                  title="Overdue Reviews"
                  action="REVIEW"
                  rows={overdueReviews}
                  emptyMessage="No positions are past their next review date."
                />
                <WorkflowQueueColumn
                  title="Upcoming Reviews"
                  action="REVIEW"
                  rows={upcomingReviews}
                  emptyMessage="No upcoming reviews in the next two weeks."
                />
              </div>
            )}
          </section>

          <section className="workflow-section">
            <div className="workflow-section-header">
              <div className="workflow-section-copy workflow-section-copy-plain">
                <h2>Action Queues</h2>
                <p>Candidate timing and owned-position drift stay visible without opening every ticker page.</p>
              </div>
              {selectedPortfolio && <div className="workflow-section-meta">{selectedPortfolio.name}</div>}
            </div>
            {selectedPortfolio == null ? (
              <div className="workflow-empty">Select an active portfolio to see candidate and owned-position queues.</div>
            ) : (
              <div className="workflow-grid workflow-grid-command">
                <WorkflowQueueColumn
                  title="Candidates Near Buy Range"
                  action="BUY"
                  rows={nearBuyCandidates}
                  emptyMessage="No active research or candidate names are near the current buy range."
                />
                <WorkflowQueueColumn
                  title="Owned Positions Needing Attention"
                  action="START_SELLING"
                  rows={ownedNeedsAttention}
                  emptyMessage="No owned positions currently need attention beyond hold."
                />
              </div>
            )}
          </section>
        </>
      )}
    </InvestmentsWorkspaceShell>
  );
};

function WorkflowQueueColumn({
  title,
  action,
  rows,
  emptyMessage,
}: {
  title: string;
  action: PortfolioAction;
  rows: AugmentedPortfolioRow[];
  emptyMessage: string;
}): JSX.Element {
  return (
    <section className="investments-card workflow-card">
      <div className="workflow-column-header">
        <div>
          <div className="workflow-column-title">{title}</div>
          <div className="workflow-column-count">{rows.length} ticker{rows.length === 1 ? "" : "s"}</div>
        </div>
        <div>{portfolioActionPill(action)}</div>
      </div>
      {rows.length === 0 ? (
        <div className="workflow-empty">{emptyMessage}</div>
      ) : (
        <div className="workflow-card-list">
          {rows.map((row) => (
            <Link key={`${title}-${row.ticker}`} className="workflow-row-link" to={equityRoute(row.ticker)}>
              <article className="workflow-row-card">
                <div className="workflow-row-top">
                  <div>
                    <div className="workflow-row-ticker">{row.ticker}</div>
                    <div className="workflow-row-company">{row.companyName}</div>
                  </div>
                  <div className="workflow-row-score">{row.score != null ? formatScoreValue(row.score) : "N/A"}</div>
                </div>
                <div className="workflow-row-meta">
                  <span>{pill(row.stage.replace(/_/g, " "))}</span>
                  <span>{pill(row.screeningDisposition ? `Screen: ${row.screeningDisposition.replace(/_/g, " ")}` : `Legacy: ${row.engineAction}`)}</span>
                  <span>{row.currentPrice != null ? `Now ${formatCurrencyNullable(row.currentPrice)}` : "Now N/A"}</span>
                  <span>{row.targetBuyPrice != null ? `Target ${formatCurrencyNullable(row.targetBuyPrice)}` : "Target N/A"}</span>
                </div>
                <p className="workflow-row-reason">{row.keyRisk || row.recommendedActionReason}</p>
              </article>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function augmentPortfolioRows(
  portfolio: Portfolio | null,
  equityByTicker: Map<string, EquityDashboardRow>,
  stages: Array<"ACTIVE_RESEARCH" | "CANDIDATE" | "OWNED">,
): AugmentedPortfolioRow[] {
  return (portfolio?.rows ?? [])
    .filter((row) => stages.includes(row.stage))
    .map((row) => {
      const dashboardRow = equityByTicker.get(row.ticker.toUpperCase());
      return {
        ...row,
        companyName: dashboardRow?.companyName ?? row.companyName,
        score: dashboardRow?.conservativeScore ?? dashboardRow?.overallScore ?? null,
        screeningDisposition: dashboardRow?.screeningDisposition ?? null,
        dataConfidence: dashboardRow?.dataConfidence ?? null,
      };
    });
}

function sortQueueRows(rows: AugmentedPortfolioRow[]): AugmentedPortfolioRow[] {
  return [...rows].sort((left, right) => comparePortfolioRows(left, right));
}

function comparePortfolioRows(left: AugmentedPortfolioRow, right: AugmentedPortfolioRow): number {
  const leftPriority = queuePriority(left);
  const rightPriority = queuePriority(right);
  if (leftPriority !== rightPriority) {
    return leftPriority - rightPriority;
  }
  const scoreDelta = (right.score ?? -1) - (left.score ?? -1);
  if (scoreDelta !== 0) {
    return scoreDelta;
  }
  return left.ticker.localeCompare(right.ticker);
}

function queuePriority(row: AugmentedPortfolioRow): number {
  if (isOverdue(row.nextReviewAt)) {
    return 0;
  }
  if (isUpcoming(row.nextReviewAt)) {
    return 1;
  }
  if (isNearBuyRange(row.currentPrice, row.targetBuyPrice)) {
    return 2;
  }
  if (row.recommendedAction !== "HOLD") {
    return 3;
  }
  return 4;
}

function portfolioActionPill(action: PortfolioAction): JSX.Element {
  return (
    <span className={`action-pill portfolio-action-${action.toLowerCase().replace(/_/g, "-")}`}>
      {actionLabel(action)}
    </span>
  );
}

function actionLabel(action: PortfolioAction): string {
  switch (action) {
    case "BUY_MORE":
      return "Buy More";
    case "START_SELLING":
      return "Start Selling";
    default:
      return action.charAt(0) + action.slice(1).toLowerCase().replace(/_/g, " ");
  }
}

function isNearBuyRange(currentPrice: number | null, targetBuyPrice: number | null): boolean {
  if (typeof currentPrice !== "number" || typeof targetBuyPrice !== "number" || targetBuyPrice <= 0) {
    return false;
  }
  return currentPrice <= targetBuyPrice * 1.12;
}

function isOverdue(value: string | null | undefined): boolean {
  if (!value) {
    return false;
  }
  const timestamp = Date.parse(value);
  if (Number.isNaN(timestamp)) {
    return false;
  }
  return timestamp < Date.now();
}

function isUpcoming(value: string | null | undefined): boolean {
  if (!value) {
    return false;
  }
  const timestamp = Date.parse(value);
  if (Number.isNaN(timestamp)) {
    return false;
  }
  const now = Date.now();
  return timestamp >= now && timestamp <= now + 14 * 24 * 60 * 60 * 1000;
}

export default InvestmentsDashboard;
